import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { MAX_RANGE_MS, MAX_USER_IDS } from './types';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export const USER_ID_TOKEN_SEPARATORS = [',', ' ', '\n', '\t'];

// Select tags may still hold a pasted blob that the separators did not split.
export const normalizeUserIDs = (tags: string[] | undefined | null): string[] => {
  const ids = (tags || [])
    .flatMap(tag => String(tag).split(/[\s,]+/))
    .map(tag => tag.trim())
    .filter(Boolean);
  return Array.from(new Set(ids));
};

export const validateUserIDs = (tags: string[] | undefined | null): string | null => {
  const ids = normalizeUserIDs(tags);
  if (!ids.length) return 'User ID is required';
  const invalid = ids.filter(id => !/^\d+$/.test(id));
  if (invalid.length) return `User IDs must be digits only: ${invalid.join(', ')}`;
  if (ids.length > MAX_USER_IDS) return `At most ${MAX_USER_IDS} user IDs are allowed`;
  return null;
};

// The picker returns browser-local values; re-read the wall-clock time in the selected zone.
export const toInstantInTimezone = (picked: Dayjs, timezone: string): Dayjs =>
  dayjs.tz(picked.format('YYYY-MM-DDTHH:mm:ss'), timezone);

export const toTraceRange = (
  range: [Dayjs, Dayjs],
  timezone: string,
): { startTime: string; endTime: string; durationMs: number } => {
  const start = toInstantInTimezone(range[0], timezone);
  const end = toInstantInTimezone(range[1], timezone);
  return {
    startTime: start.toISOString(),
    endTime: end.toISOString(),
    durationMs: end.diff(start),
  };
};

export const validateRange = (
  range: [Dayjs, Dayjs] | undefined | null,
  timezone: string | undefined,
): string | null => {
  if (!range || !range[0] || !range[1]) return 'Date range is required';
  if (!timezone) return null;
  const { durationMs } = toTraceRange(range, timezone);
  if (durationMs <= 0) return 'End time must be after start time';
  if (durationMs > MAX_RANGE_MS) return 'The range must be 31 days or less';
  return null;
};
